import { notEmpty, useSurveyMetasQuery } from '@cyren/common-lib';
import { uniq } from 'lodash';
import { useMemo } from 'react';
import { useIsV2Path } from '../app/route-utils';
import { SurveyMetaQna } from '../graphql-enhanced';
import { Keys } from '../report/types';
import { useSurveyMetaControllerFindBySurvey } from '../v2/api/generated/survey-meta/survey-meta';

export function useSurveyMeta({
  metaKey,
  surveyId,
  surveyShortId,
}: {
  metaKey: string;
  surveyId?: string | null;
  surveyShortId?: string | null;
}) {
  const isV2Path = useIsV2Path();
  const hasSurveyId = notEmpty(surveyId);
  const hasSurveyShortId = notEmpty(surveyShortId);
  const parsedSurveyId = hasSurveyId ? Number(surveyId) : Number.NaN;
  const hasValidParsedSurveyId = Number.isFinite(parsedSurveyId);

  const { data: dataBySurveyId, loading: loadingBySurveyId } = useSurveyMetasQuery({
    skip: isV2Path || !hasSurveyId,
    variables: {
      filters: {
        survey: {
          id: {
            eq: surveyId,
          },
        },
        metaKey: {
          eq: metaKey,
        },
      },
    },
  });

  const { data: dataBySurveyShortId, loading: loadingBySurveyShortId } = useSurveyMetasQuery({
    skip: isV2Path || !hasSurveyShortId,
    variables: {
      filters: {
        survey: {
          short_id: {
            eq: surveyShortId,
          },
        },
        metaKey: {
          eq: metaKey,
        },
      },
    },
  });

  const {
    data: v2MetaData,
    isLoading: loadingV2Meta,
  } = useSurveyMetaControllerFindBySurvey(
    {
      surveyId: parsedSurveyId,
      metaKey,
    },
    {
      query: {
        enabled: isV2Path && hasValidParsedSurveyId,
      },
    },
  );

  const data = notEmpty(dataBySurveyId?.surveyMetas?.data) ? dataBySurveyId : dataBySurveyShortId;

  const loadingV1Meta =
    (hasSurveyId && loadingBySurveyId) ||
    (!notEmpty(dataBySurveyId?.surveyMetas?.data) && hasSurveyShortId && loadingBySurveyShortId);

  // INIT FORM DATA
  const meta = isV2Path
    ? v2MetaData && {
      id: `${v2MetaData.id}`,
      attributes: {
        metaKey: v2MetaData.metaKey,
        metaType: v2MetaData.metaType,
        metadata: v2MetaData.metadata,
      },
    }
    : data?.surveyMetas?.data?.[0];

  const metaItems: SurveyMetaQna['items'] = isV2Path
    ? (((v2MetaData?.metadata || {}) as SurveyMetaQna).items || [])
    : (meta?.attributes?.metadata?.items || []);

  const transKeys = useMemo(() => {
    const tKeys = metaItems.reduce((p, item) => {
      return [...p, item.qnaQuestionKey, item.qnaAnswerKey];
    }, [] as Keys);

    return uniq(tKeys.filter(notEmpty));
  }, [metaItems]);

  const loading = isV2Path ? loadingV2Meta : loadingV1Meta;

  return { meta, data, loading, transKeys, metaItems } as const;
}
